import React, { useState } from 'react';

function CodeBlock({ code, language }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="code-block">
      <button className="code-copy-btn" onClick={handleCopy}>{copied ? 'Copied!' : 'Copy'}</button>
      <pre><code>{code}</code></pre>
    </div>
  );
}

function Section({ title, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="card">
      <div className="section-header" onClick={() => setOpen(!open)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>{title}</h3>
        <span>{open ? '−' : '+'}</span>
      </div>
      {open && <div className="section-content" style={{ marginTop: 16 }}>{children}</div>}
    </div>
  );
}

export default function Usage() {
  return (
    <div>
      <h2 className="page-title">Usage Documentation</h2>

      <div className="card">
        <p>This guide explains how to use images built by the Image Factory in your infrastructure deployments.</p>
      </div>

      <Section title="Using Images with Terraform" defaultOpen={true}>
        <h4>EC2 Instance</h4>
        <CodeBlock language="hcl" code={`# Look up the latest golden image
data "aws_ami" "golden_image" {
  most_recent = true
  owners      = ["self"]

  filter {
    name   = "name"
    values = ["if-*"]
  }

  filter {
    name   = "tag:ImageType"
    values = ["golden_image"]
  }

  filter {
    name   = "tag:Blueprint"
    values = ["amazonlinux2023"]
  }
}

# Launch an EC2 instance using the golden image
resource "aws_instance" "example" {
  ami           = data.aws_ami.golden_image.id
  instance_type = "t3.medium"

  tags = {
    Name = "my-server"
  }
}`} />

        <h4 style={{ marginTop: 24 }}>Auto Scaling Group</h4>
        <CodeBlock language="hcl" code={`resource "aws_launch_template" "example" {
  name_prefix   = "my-app-"
  image_id      = data.aws_ami.golden_image.id
  instance_type = "t3.medium"

  tag_specifications {
    resource_type = "instance"
    tags = {
      Name = "my-app-instance"
    }
  }
}

resource "aws_autoscaling_group" "example" {
  desired_capacity   = 2
  max_size           = 5
  min_size           = 1
  vpc_zone_identifier = var.subnet_ids

  launch_template {
    id      = aws_launch_template.example.id
    version = "$Latest"
  }
}`} />
      </Section>

      <Section title="Using Images with AWS CLI">
        <h4>Find Latest Image</h4>
        <CodeBlock language="bash" code={`# List all golden images
aws ec2 describe-images \\
  --owners self \\
  --filters "Name=tag:ImageType,Values=golden_image" \\
  --query 'Images | sort_by(@, &CreationDate) | [-1].ImageId' \\
  --output text

# Find image by blueprint name
aws ec2 describe-images \\
  --owners self \\
  --filters "Name=tag:Blueprint,Values=ubuntu22.04-lts" \\
  --query 'Images | sort_by(@, &CreationDate) | [-1].[ImageId,Name,CreationDate]' \\
  --output table`} />

        <h4 style={{ marginTop: 24 }}>Launch Instance from Image</h4>
        <CodeBlock language="bash" code={`AMI_ID=$(aws ec2 describe-images \\
  --owners self \\
  --filters "Name=tag:Blueprint,Values=amazonlinux2023" \\
  --query 'Images | sort_by(@, &CreationDate) | [-1].ImageId' \\
  --output text)

aws ec2 run-instances \\
  --image-id $AMI_ID \\
  --instance-type t3.medium \\
  --key-name my-key \\
  --subnet-id subnet-xxx \\
  --tag-specifications 'ResourceType=instance,Tags=[{Key=Name,Value=my-server}]'`} />
      </Section>

      <Section title="Using Container Images from ECR">
        <CodeBlock language="bash" code={`# Authenticate to ECR
aws ecr get-login-password --region us-east-1 | \\
  docker login --username AWS --password-stdin \\
  447756393901.dkr.ecr.us-east-1.amazonaws.com

# Pull container image
docker pull 447756393901.dkr.ecr.us-east-1.amazonaws.com/image-factory-frontend:latest`} />
      </Section>

      <Section title="Creating Custom Blueprints">
        <p>Blueprints are Packer HCL templates stored in S3. To create a custom blueprint:</p>
        <ol style={{ marginTop: 12, paddingLeft: 20 }}>
          <li>Create a Packer template following the standard structure</li>
          <li>Upload via the Blueprints page or directly to S3</li>
          <li>Trigger a build using the Build page</li>
        </ol>

        <h4 style={{ marginTop: 16 }}>Blueprint Template Structure</h4>
        <CodeBlock language="hcl" code={`# my-blueprint/my-blueprint.pkr.hcl
packer {
  required_plugins {
    amazon = {
      source  = "github.com/hashicorp/amazon"
      version = "~> 1"
    }
  }
}

source "amazon-ebs" "my_image" {
  ami_name      = "if-my-blueprint-{{timestamp}}"
  instance_type = var.instance_type
  region        = var.aws_region

  source_ami_filter {
    filters = {
      name                = "amzn2-ami-hvm-*-x86_64-gp2"
      root-device-type    = "ebs"
      virtualization-type = "hvm"
    }
    most_recent = true
    owners      = ["137112412989"]
  }

  tags = {
    Blueprint  = "my-blueprint"
    ImageType  = "golden_image"
    ManagedBy  = "image-factory"
  }
}

build {
  sources = ["source.amazon-ebs.my_image"]

  provisioner "shell" {
    inline = [
      "sudo yum update -y",
      "sudo yum install -y your-packages"
    ]
  }
}`} />
      </Section>

      <Section title="Using Channels with Terraform (Recommended)">
        <p>Channels provide a stable reference to the latest approved image. Use the HTTP data source to look up the current AMI for a channel.</p>
        <h4>HTTP Data Source</h4>
        <CodeBlock language="hcl" code={`# Look up AMI from Image Factory channel
data "http" "golden_ami" {
  url = "\${var.image_factory_url}/api/v1/channels/stable/ami?region=\${var.aws_region}"
  request_headers = {
    Authorization = "Bearer \${var.api_token}"
  }
}

locals {
  golden_ami_id = jsondecode(data.http.golden_ami.response_body).ami_id
}

resource "aws_instance" "example" {
  ami           = local.golden_ami_id
  instance_type = "t3.medium"
}`} />

        <h4 style={{ marginTop: 24 }}>Available Channels</h4>
        <ul style={{ paddingLeft: 20, color: '#636e72' }}>
          <li><code>latest</code> — Most recent successful build</li>
          <li><code>stable</code> — Production-verified images</li>
          <li><code>hardened-*</code> — CIS-hardened quarterly releases</li>
        </ul>

        <h4 style={{ marginTop: 24 }}>Custom Terraform Provider (Advanced)</h4>
        <CodeBlock language="hcl" code={`# Using the Image Factory Terraform Provider
terraform {
  required_providers {
    imagefactory = {
      source  = "altium/imagefactory"
      version = "~> 1.0"
    }
  }
}

provider "imagefactory" {
  endpoint = var.image_factory_url
  token    = var.api_token
}

data "imagefactory_channel" "stable" {
  name   = "stable"
  region = var.aws_region
}

resource "aws_instance" "example" {
  ami           = data.imagefactory_channel.stable.ami_id
  instance_type = "t3.medium"
}`} />
      </Section>

      <Section title="API Reference">
        <p>The Image Factory exposes a GraphQL API for programmatic access.</p>
        <h4 style={{ marginTop: 16 }}>Query Images</h4>
        <CodeBlock language="graphql" code={`query {
  listImages(limit: 10) {
    items {
      id
      name
      blueprintName
      status
      createdAt
      platforms {
        region
        imageId
        compliant
      }
    }
    nextToken
  }
}`} />

        <h4 style={{ marginTop: 16 }}>Trigger Build</h4>
        <CodeBlock language="graphql" code={`mutation {
  triggerBuild(input: {
    blueprintName: "amazonlinux2023"
    blueprintVersion: "latest"
    imageType: "golden_image"
    awsRegion: "us-east-1"
    instanceType: "t3.medium"
  }) {
    workflowName
    status
    message
  }
}`} />
      </Section>
    </div>
  );
}
